<template>
  <PortalWidget
    :widget="widgetTitle"
  >
    <!-- Table Container -->
    <div
      v-if="!isFetchingCreditCards"
      class="grid_table_holder"
      data-testid="credit-cards"
    >
      <div class="m-b-15 m-t-10">
        <button
          type="button"
          class="btn btn-sm btn-success"
          data-testid="add-credit-card-button"
          @click="addCard"
        >
          <i class="typcn typcn-plus" />
          {{ $t('ACCOUNT_BILLING_CC_ADD_CARD') }}
        </button>
      </div>
      <v-table
        :data="creditCards"
        :current-page.sync="currentPage"
        :page-size="20"
        :hide-sort-icons="true"
        class="table_beauty credit-cards-list"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <v-th
              sort-key="default"
              v-t="'ACCOUNT_BILLING_CC_DEFAULT'"
            />
            <v-th
              sort-key="name"
              v-t="'ACCOUNT_BILLING_ADD_CC_CC_NAME'"
            />
            <v-th
              sort-key="cardType"
              v-t="'ACCOUNT_BILLING_CC_TYPE'"
            />
            <v-th
              sort-key="cC_Last_4"
              v-t="'ACCOUNT_BILLING_CC_NUMBER'"
            />
            <v-th
              sort-key="expiry"
              v-t="'ACCOUNT_BILLING_CC_EXPIRATION'"
            />
            <v-th
              sort-key="country"
              v-t="'ACCOUNT_BILLING_CC_COUNTRY'"
            />
            <v-th
              sort-key="status"
              v-t="'ACCOUNT_BILLING_CC_STATUS'"
            />
            <v-th
              sort-key="lastFailed"
              v-t="'ACCOUNT_BILLING_LAST_FAILED'"
            />
            <th v-t="'ACCOUNT_BILLING_CC_ACTIONS'" />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <tr v-if="!creditCards.length">
            <td colspan="100" v-t="'ACCOUNT_BILLING_CC_EMPTY'"></td>
          </tr>

          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              :key="row.refId"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <td>
                <span v-if="row.default">
                  <i
                    v-tooltip.right="$t('ACCOUNT_BILLING_CC_DEFAULT_TT')"
                    aria-hidden="true"
                    class="typcn typcn-star-full-outline lemon-yellow"
                  />
                </span>
                <span v-else class="clickable">
                  <i
                    v-tooltip.right="$t('ACCOUNT_BILLING_CC_MAKE_DEFAULT_TT')"
                    aria-hidden="true"
                    class="typcn typcn-star-full-outline"
                    @click="makeDefault(row)"
                  />
                </span>
              </td>
              <td>
                {{ row.name }}
              </td>
              <td>
                <i class="credit-card-img" :class="formatCardType(row.cardType)"></i>
              </td>
              <td>
                xxxx-xxxx-xxxx-{{ row.lastFour }}
              </td>
              <td>
                {{ displayExpiration(row) }}
              </td>
              <td>
                <span
                  v-if="row.cardAddress && row.cardAddress.countryCode"
                  class="flag"
                  :class="row.cardAddress.countryCode.toLowerCase()"
                  v-tooltip.right="row.cardAddress.countryName"
                />
              </td>
              <td class="status">
                <span
                  :class="{'success': row.status == 'Approved', 'warning': row.status == 'ApprovalPending', 'danger': row.status == 'Declined' || row.status == 'Expired'}"
                >
                  {{ row.status == 'ApprovalPending' ? 'Review Pending' : row.status }}
                </span>
              </td>
              <td>
                <span v-if="row.ccFailure">
                  {{ formatDate(row.ccFailure.lastFailureDT) }}

                  <i 
                    v-tooltip.right="`Failure message: ${row.ccFailure.lastError}`"
                    aria-hidden="true"
                    class="fa fa-info-circle m-l-5">
                  </i>
                </span>
              </td>
              <td>
                <CreditCardsActions
                  :credit-card="row"
                  :disabled="disableActions"
                  @delete-card="deleteCard(row)"
                  @verify-card="verifyCard(row)"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      <div v-if="creditCards && creditCards.length" class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="7"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'LOADING'" />
    </div>
    <ConfirmationModal
      v-if="showAddModal"
      :custom-classes="'size-lg add-credit-card-modal'"
      :title="$t('ACCOUNT_BILLING_ADD_CC_HEADER')"
      :show-confirm-button="false"
      :show-cancel-button="false"
      :allow-modal-to-be-closed="false"
      @close="showAddModal = false"
    >
      <AddCreditCardForm
        @closeModal="showAddModal = false"
      />
    </ConfirmationModal>
    <ConfirmationModal
      v-if="showVerifyModal"
      :custom-classes="'size-lg verify-credit-card-modal'"
      :title="$t('ACCOUNT_BILLING_CC_VERIFY_MODAL_HEADER')"
      :show-confirm-button="false"
      :show-cancel-button="false"
      :allow-modal-to-be-closed="false"
      @close="showVerifyModal = false"
    >
      <VerifyCreditCardForm
        :selected-card="selectedCardToVerify"
        @cancel="closeVerifyModal"
      />
    </ConfirmationModal>
    <ConfirmationModal
      v-if="showDeleteModal"
      :title="$t('ACCOUNT_BILLING_CC_REMOVE_MODAL_HEADER')"
      :cancel-button-text="$t('CANCEL')"
      :confirm-action-text="$t('ACCOUNT_BILLING_CC_REMOVE_MODAL_YES')"
      :confirm-button-loading="isDeletingCreditCard"
      @confirm="deleteCardConfirm"
      @close="closeDeleteModal"
    >
      <p>{{ deleteModalMessage }}</p>
    </ConfirmationModal>
  </PortalWidget>
</template>

<script>
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import { utcToLocalFormat } from '@/core/filters/utcToLocalFormat';
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { mapState, mapGetters, mapActions } from 'vuex';
import CreditCardsActions from './CreditCardsActions';
import AddCreditCardForm from './AddCreditCardForm';
import VerifyCreditCardForm from './VerifyCreditCardForm';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';

export default {
  name: 'CreditCards',
  components: {
    CreditCardsActions,
    AddCreditCardForm,
    VerifyCreditCardForm,
    ConfirmationModal
  },
  data() {
    return {
      widgetTitle: { title: this.$t('ACCOUNT_BILLING_CC_HEADER'), iconClass: 'typcn typcn-credit-card' },
      currentPage: 1,
      totalPages: 0,
      showAddModal: false,
      showVerifyModal: false,
      showDeleteModal: false,
      deleteModalMessage: '',
      selectedCardToDelete: '',
      selectedCardToVerify: ''
    };
  },
  computed: {
    ...mapGetters('billingSettingsStore', [
      'getCreditCardsStatus',
      'deleteCreditCardStatus',
      'updateCreditCardStatus'
    ]),
    ...mapState({
      accountId: 'currentAccountId',
    }),
    ...mapState('billingSettingsStore', ['creditCards']),
    disableActions() {
      return this.isDeletingCreditCard;
    },
    isDeletingCreditCard() {
      return this.deleteCreditCardStatus.status === STATUS_PENDING;
    },
    isFetchingCreditCards() {
      return this.getCreditCardsStatus.status === STATUS_PENDING;
    }
  },
  created() {
    this.getCreditCards();
  },
  methods: {
    ...mapActions('billingSettingsStore', [
      'getCreditCards',
      'deleteCreditCard',
      'updateCreditCard'
    ]),
    addCard() {
      this.showAddModal = true;
    },
    verifyCard(creditCard) {
      this.showVerifyModal = true;
      this.selectedCardToVerify = creditCard.id;
    },
    async deleteCard(creditCard) {
      this.showDeleteModal = true;
      this.deleteModalMessage = `${this.$t('ACCOUNT_BILLING_CC_REMOVE_MODAL_BODY_1')} ${creditCard.cardType} ${this.$t('ACCOUNT_BILLING_CC_REMOVE_MODAL_BODY_2')} ${creditCard.lastFour}?`;
      this.selectedCardToDelete = creditCard.id;
    },
    async deleteCardConfirm() {
      await this.deleteCreditCard(this.selectedCardToDelete);
      if (this.deleteCreditCardStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('ACCOUNT_BILLING_CC_REMOVE_SUCCESS'));
        this.closeDeleteModal();
      }
      if (this.deleteCreditCardStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.deleteCreditCardStatus.error);
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteModalMessage = '';
      this.selectedCardToDelete = '';
    },
    closeVerifyModal() {
      this.showVerifyModal = false;
      this.selectedCardToVerify = '';
    },
    async makeDefault(creditCard) {
      const payload = {
        data: {
          setAsDefault: true
        },
        creditCardId: creditCard.id
      };
      await this.updateCreditCard(payload);
      if (this.updateCreditCardStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, `${creditCard.lastFour} ${this.$t('ACCOUNT_BILLING_CC_MAKE_DEFAULT_SUCCESS')}`);
      }
      if (this.deleteCreditCardStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.updateCreditCardStatus.error);
      }
    },
    formatCardType(cardType) {
      if (cardType) {
        return cardType.toLowerCase();
      }
      return cardType;
    },
    displayExpiration(creditCard) {

      // Don't mutate the original object
      const expYear = String(creditCard.expYear);
      const expMonth = String(creditCard.expMonth);
      
      // Validate input with proper sanitization
      if (!/^\d{2,4}$/.test(expYear)) {
        utilities.showToaster('error', 'Invalid expiration year format');
        return '**/**';
      }
      
      if (!/^\d{1,2}$/.test(expMonth)) {
        utilities.showToaster('error', 'Invalid expiration month format');
        return '**/**';
      }
      
      // Format with proper padding
      const formattedMonth = expMonth.padStart(2, '0'); //
      const formattedYear = expYear.slice(-2);
      
      return `${formattedMonth}/${formattedYear}`;
    },
    formatDate(date) {
      if (!date) {
        return '';
      } else {
        return utcToLocalFormat(date);
      }
    },
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>